import { Record } from 'immutable';
import type { ReactElement, ReactNode, SyntheticEvent } from 'react';

import type { StdDialogCloseReason, StdDialogProps, StdDialogSize } from 'components/Dialogs/StdDialog/types';
import type { QButtonVariant } from 'components/QButton';

/** The reason each button reports. */
export type ConfirmationDialogButton = 'CONFIRM' | 'DENY' | 'APPLY_TO_ALL';

export type ConfirmationDialogReason = ConfirmationDialogButton | StdDialogCloseReason;

export type ConfirmationDialogAction = (reason?: ConfirmationDialogReason, closeEvent?: SyntheticEvent | null) => void;

export type ConfirmationDialogProps = {
  title: string | ReactElement;
  content: ReactNode;
  /** Renders the view registered under this id below the content. */
  contentViewId?: string | null;

  confirmLabel?: string;
  denyLabel?: string | null;
  hideBackdrop?: boolean;
  showCloseButton?: boolean;

  confirmAction: ConfirmationDialogAction | null;
  denyAction?: ConfirmationDialogAction | null;
  tertiaryAction?: ConfirmationDialogAction | null;
  /** Runs before the action. Return false to keep the dialog open. */
  onClose?: (closeEvent: SyntheticEvent | null, reason: ConfirmationDialogReason) => boolean | void;

  /** The button that closing with the X, Escape or the backdrop counts as. */
  dialogCloseAliasAction?: ConfirmationDialogButton;
  size?: StdDialogSize | null;
  confirmStartIcon?: ReactElement | null;

  confirmButtonVariant?: QButtonVariant;
  denyButtonVariant?: QButtonVariant;
  tertiaryLabelVariant?: QButtonVariant;

  tertiaryLabel?: string | null;
  /** Keeps Confirm disabled until this text is typed. */
  textEntryConfirmationText?: string | null;
  unregisterContentViewOnClose?: boolean;
  sx?: StdDialogProps['sx'];
};

export const mkConfirmationDialogProps = Record<ConfirmationDialogProps>({
  title: '',

  content: '',
  contentViewId: null,
  size: null,

  confirmLabel: 'OK',
  confirmButtonVariant: 'contained',
  confirmStartIcon: null,

  denyLabel: 'Cancel',
  denyButtonVariant: 'outlined',

  confirmAction: null,
  denyAction: null,
  onClose: undefined,
  hideBackdrop: false,
  showCloseButton: true,

  dialogCloseAliasAction: 'DENY',

  textEntryConfirmationText: null,
  unregisterContentViewOnClose: false,

  tertiaryLabel: null,
  tertiaryAction: null,
  tertiaryLabelVariant: 'delete',
  sx: undefined,
});

export const DIALOG_TYPE = 'DIALOG_CONFIRMATION';
